import { EDIT_REDACTIONS_SKIP_CODES as SKIP_CODES, REDACTION_OCCURRENCE_ACTION } from 'citadel-shared/redactionEditing.js'

const titleCase = status =>
  String(status ?? '')
    .toLowerCase()
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')

const isNotSearched = entry => entry?.code === SKIP_CODES.NOT_AT_REDACTION_STAGE

export function humanizeSkip(entry = {}) {
  switch (entry.code) {
    case SKIP_CODES.NOT_AT_REDACTION_STAGE:
      return `no redactions noted yet — ${titleCase(entry.status)}`
    case SKIP_CODES.NO_NOTED_FILE:
      return 'no redactions-noted PDF'
    case SKIP_CODES.MULTIPLE_NOTED_FILES:
      return 'more than one redactions-noted PDF'
    case SKIP_CODES.PENDING_WORKFLOW:
      return `another workflow is running${entry.workflowType ? ` (${titleCase(entry.workflowType)})` : ''}`
    case SKIP_CODES.STALE_VERSION:
      return 'the file was changed by someone else — reload to continue'
    default:
      return entry.reason ?? ''
  }
}

// Build display text for a completed redact-all operation.
export function redactAllSummary({
  added,
  removed = null,
  changed = false,
  action = REDACTION_OCCURRENCE_ACTION.REDACT,
  response,
  categoryId,
  failed = [],
  truncated = false,
  maxBoxes = null,
  ocrAdded = 0,
  ocrSkippedPages = 0
}) {
  const quoted = `“${response.text}”`
  const isUnredacting = action === REDACTION_OCCURRENCE_ACTION.UNREDACT
  const lead = isUnredacting
    ? removed > 0
      ? `Un-redacted ${removed} ${removed === 1 ? 'occurrence' : 'occurrences'} of ${quoted} in this file.`
      : changed
        ? `Un-redacted matching occurrences of ${quoted} in this file.`
        : `No redacted occurrences of ${quoted} were found in this file.`
    : added === 0
      ? `No new occurrences of ${quoted} in this file.`
      : `Added ${added} ${added === 1 ? 'box' : 'boxes'} for ${quoted} in this file.`
  const details = []
  const ocrOfAdded = Math.min(Math.max(ocrAdded, 0), Math.max(added, 0))
  if (ocrOfAdded > 0) {
    details.push(ocrOfAdded === 1 ? '1 of them was found on a scanned page.' : `${ocrOfAdded} of them were found on scanned pages.`)
  }
  if (truncated) {
    details.push(`Stopped at the ${maxBoxes ? `${maxBoxes}-box` : 'box'} limit for this file — some occurrences were not redacted.`)
  }
  if (response.scope === 'request') {
    const others = response.started.filter(s => s.categoryId !== categoryId && !failed.some(f => f.categoryId === s.categoryId))
    if (others.length > 0) {
      const label = others.length === 1 ? '1 other category' : `${others.length} other categories`
      details.push(`Also updated ${label}: ${others.map(o => o.categoryName).join(', ')}.`)
    }
    const notSearched = response.skipped.filter(isNotSearched)
    const skipped = response.skipped.filter(sk => !isNotSearched(sk))
    if (skipped.length > 0) {
      details.push(`Skipped ${skipped.length}: ${skipped.map(sk => `${sk.categoryName} (${humanizeSkip(sk)})`).join('; ')}.`)
    }
    if (notSearched.length > 0) {
      details.push(`Not searched (no redactions noted yet): ${notSearched.map(sk => `${sk.categoryName} — ${titleCase(sk.status)}`).join(', ')}.`)
    }
  }
  if (ocrSkippedPages > 0) {
    const pages = ocrSkippedPages === 1 ? '1 page' : `${ocrSkippedPages} pages`
    details.push(`${pages} could not be fully searched; occurrences there may have been missed.`)
  }
  if (failed.length > 0) details.push(`Failed in ${failed.map(f => `${f.categoryName}${f.errorMessage ? ` (${f.errorMessage})` : ''}`).join(', ')}.`)
  return {
    lead,
    details,
    truncated: Boolean(truncated),
    ocrSkippedPages,
    warning: Boolean(truncated) || ocrSkippedPages > 0,
    text: [lead, ...details].join(' ')
  }
}
